import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import {
  amount,
  downloadReportCsv,
  downloadReportPdf,
  posApi,
  reportPeriodLabel,
  type SalesReport as SalesReportData,
  type SalesReportGroup,
  type SalesReportTransfers,
} from '../api/pos';
import { billTime, money } from '../format';

const STORE_NAME = import.meta.env.VITE_STORE_NAME || 'Blossom Books';

interface Props {
  /** IST calendar days, `YYYY-MM-DD`. Omitted means today. */
  from?: string;
  to?: string;
  /**
   * Whether staff can change the range from inside the report. Off on the
   * day's cash-up, where the whole point is that it is today's figures and
   * nothing else; on in history, where any period can be reported.
   */
  rangePicker?: boolean;
  onClose: () => void;
}

/**
 * The counter register: every book that left the shop over a period, with the
 * bill it went out on, split by how it was paid for.
 *
 * A different question from the bill lists behind it. Those show which bills
 * were raised; this shows what was in them — the sheet the shop reconciles
 * stock and takings against at the end of a day or a fair. Card and UPI are
 * one section because they both settle into the bank on the machine's own
 * statement; cash is its own because it has to be counted out of the drawer
 * by hand.
 *
 * The figures are the server's, not this component's: the history list pages
 * at 25 bills, so adding up what is on screen would report a page rather than
 * the period.
 */
export default function SalesReport({ from, to, rangePicker = false, onClose }: Props) {
  const [rangeFrom, setRangeFrom] = useState(from ?? '');
  const [rangeTo, setRangeTo] = useState(to ?? '');
  const [data, setData] = useState<SalesReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'pdf' | 'csv' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      posApi
        .salesReport({ from: rangeFrom || undefined, to: rangeTo || undefined }, signal)
        .then((res) => {
          setData(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setError(errorMessage(err, 'Could not build the report.'));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [rangeFrom, rangeTo],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const save = async (as: 'pdf' | 'csv') => {
    if (!data) return;
    setSaving(as);
    setSaveError(null);
    try {
      if (as === 'pdf') await downloadReportPdf(data);
      else downloadReportCsv(data, STORE_NAME);
    } catch (err) {
      // The report is already on screen, so a failed download is a retryable
      // notice rather than an error about the figures.
      setSaveError(errorMessage(err, 'Could not save the report.'));
    } finally {
      setSaving(null);
    }
  };

  const totals = data?.totals;
  const empty = !!data && data.totals.bills === 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Sales report"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="report">
          <header className="report-head">
            <h2>{STORE_NAME} — Sales report</h2>
            <p>
              {data ? reportPeriodLabel(data.from, data.to) : '…'}
              {data?.staffName && ` · ${data.staffName}`}
            </p>
          </header>

          {rangePicker && (
            <div className="report-range">
              <label className="field">
                <span>From</span>
                <input
                  type="date"
                  value={rangeFrom}
                  max={rangeTo || undefined}
                  onChange={(e) => setRangeFrom(e.target.value)}
                />
              </label>
              <label className="field">
                <span>To</span>
                <input
                  type="date"
                  value={rangeTo}
                  min={rangeFrom || undefined}
                  onChange={(e) => setRangeTo(e.target.value)}
                />
              </label>
            </div>
          )}

          {totals && (
            <div className="report-summary">
              <div>
                <span className="cashup-label">Bills</span>
                <strong>{totals.bills}</strong>
              </div>
              <div>
                <span className="cashup-label">Titles</span>
                <strong>{totals.titles}</strong>
              </div>
              <div>
                <span className="cashup-label">Copies</span>
                <strong>{totals.quantity}</strong>
              </div>
              {/* Only on a period where something was actually given away.
                  On every other report this is a "₹0 off" cell taking up a
                  quarter of the strip to say nothing happened. */}
              {amount(totals.discount) > 0 && (
                <div>
                  <span className="cashup-label">Discount</span>
                  <strong>− {money(amount(totals.discount))}</strong>
                </div>
              )}
              <div className="cashup-gross">
                {/* The money, so it is net of anything discounted. The gross
                    is on the grand total row below, where it has to be the
                    sum of the lines printed above it. */}
                <span className="cashup-label">Total taken</span>
                <strong>{money(amount(totals.net ?? totals.amount))}</strong>
              </div>
            </div>
          )}

          {error && <p className="error">{error}</p>}

          {loading ? (
            <p className="muted">Building the report…</p>
          ) : empty ? (
            <p className="muted empty">Nothing was sold in this period.</p>
          ) : (
            data && (
              <>
                {data.groups.map((group, i) => (
                  <ReportSection key={group.key} index={i + 1} group={group} />
                ))}

                <table className="report-table report-grand">
                  <tfoot>
                    <tr>
                      <td colSpan={4}>Grand total</td>
                      <td className="num">{data.totals.quantity}</td>
                      <td className="num">{money(amount(data.totals.amount))}</td>
                    </tr>
                    {/* The discount cannot be folded into the row above: that
                        one adds up the lines printed above IT, and a column
                        that stops being a sum is a column nobody can check. */}
                    {amount(data.totals.discount) > 0 && (
                      <>
                        <tr className="report-grand-sub">
                          <td colSpan={5}>Less discounts given at the counter</td>
                          <td className="num">
                            − {money(amount(data.totals.discount))}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={5}>Net taken</td>
                          <td className="num">
                            {money(amount(data.totals.net ?? data.totals.amount))}
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
              </>
            )
          )}

          {/* At the foot, below every tender section and outside all of them.
              Books that left the shelf for the other shop are not takings, but
              they are stock that moved, and a report that simply omitted them
              would leave the shelf count unexplained. */}
          {!!data?.transfers?.totals.bills && (
            <TransferSection transfers={data.transfers} />
          )}

          {/* Said out loud rather than left as a silent gap: the report and the
              bill list will not agree on the count otherwise, and staff would
              be right to distrust whichever they checked second. */}
          {!!totals?.voided && (
            <p className="report-note">
              {totals.voided === 1
                ? '1 cancelled bill is not counted above.'
                : `${totals.voided} cancelled bills are not counted above.`}
            </p>
          )}

          {saveError && <p className="error">{saveError}</p>}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn"
            disabled={!data || empty || saving !== null}
            onClick={() => void save('csv')}
          >
            {saving === 'csv' ? 'Saving…' : 'Excel (CSV)'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!data || empty || saving !== null}
            onClick={() => void save('pdf')}
          >
            {saving === 'pdf' ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * One payment bucket.
 *
 * The bill number is printed once per bill rather than on every line: three
 * books on one bill are one purchase, and repeating the number three times
 * buries that under a column of near-identical strings.
 */
/**
 * Shop transfers, at the foot of the report.
 *
 * Deliberately has no Amount column at all rather than a column of zeroes: a
 * zero reads as "sold for nothing" and invites somebody to total it, when the
 * whole point is that no money was involved. Copies are the only figure here.
 */
function TransferSection({ transfers }: { transfers: SalesReportTransfers }) {
  let lastBill: string | null = null;

  return (
    <section className="report-section report-transfers">
      <h3>Shop transfer</h3>
      <p className="muted small">
        Moved to the other shop, not sold — in none of the figures above.
      </p>

      <div className="report-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="report-bill">Bill no.</th>
              <th>Book title</th>
              <th className="report-time">Time</th>
              <th className="report-staff">Moved by</th>
              <th className="num">Qty</th>
            </tr>
          </thead>
          <tbody>
            {transfers.rows.map((row, i) => {
              const newBill = row.billNumber !== lastBill;
              lastBill = row.billNumber;
              return (
                <tr
                  key={`${row.billId}-${row.bookId}-${i}`}
                  className={newBill && i > 0 ? 'report-bill-start' : undefined}
                >
                  <td className="report-bill">{newBill ? row.billNumber : ''}</td>
                  <td>
                    <span className="report-title">{row.title}</span>
                    {(row.sku ?? row.isbn13) && (
                      <span className="report-code">{row.sku ?? row.isbn13}</span>
                    )}
                  </td>
                  <td className="report-time">{newBill ? billTime(row.movedAt) : ''}</td>
                  <td className="report-staff">
                    {newBill ? (row.staffName ?? '—') : ''}
                  </td>
                  <td className="num">{row.quantity}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>
                Transferred · {transfers.totals.bills}{' '}
                {transfers.totals.bills === 1 ? 'bill' : 'bills'},{' '}
                {transfers.totals.titles}{' '}
                {transfers.totals.titles === 1 ? 'title' : 'titles'}
              </td>
              <td className="num">{transfers.totals.quantity}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function ReportSection({ index, group }: { index: number; group: SalesReportGroup }) {
  let lastBill: string | null = null;

  return (
    <section className="report-section">
      <h3>
        <span className="report-section-no">{index}</span>
        {group.label}
      </h3>

      {group.rows.length === 0 ? (
        <p className="muted empty">No sales taken this way in this period.</p>
      ) : (
        <div className="report-scroll">
          <table className="report-table">
            <thead>
              <tr>
                <th className="report-bill">Bill no.</th>
                <th>Book title</th>
                <th className="report-time">Time</th>
                <th className="report-staff">Billed by</th>
                <th className="num">Qty</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row, i) => {
                const newBill = row.billNumber !== lastBill;
                lastBill = row.billNumber;
                return (
                  <tr
                    key={`${row.billId}-${row.bookId}-${i}`}
                    className={newBill && i > 0 ? 'report-bill-start' : undefined}
                  >
                    <td className="report-bill">{newBill ? row.billNumber : ''}</td>
                    <td>
                      <span className="report-title">{row.title}</span>
                      {(row.sku ?? row.isbn13) && (
                        <span className="report-code">{row.sku ?? row.isbn13}</span>
                      )}
                    </td>
                    <td className="report-time">{newBill ? billTime(row.soldAt) : ''}</td>
                    {/* Once per bill, like the number and the time beside it:
                        the staff member sold the bill, not each line on it. An
                        em dash where the account has since been deleted, so a
                        missing name never reads as "same as the row above". */}
                    <td className="report-staff">
                      {newBill ? (row.staffName ?? '—') : ''}
                    </td>
                    <td className="num">{row.quantity}</td>
                    <td className="num">{money(amount(row.amount))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>
                  {group.label} total · {group.totals.bills}{' '}
                  {group.totals.bills === 1 ? 'bill' : 'bills'}
                </td>
                <td className="num">{group.totals.quantity}</td>
                <td className="num">{money(amount(group.totals.amount))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
